import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import parseISO from "date-fns/parseISO";

import ContainerButton from "@kikoff/components/src/v1/buttons/ContainerButton";
import KButton from "@kikoff/components/src/v1/buttons/KButton";
import { webRPC } from "@kikoff/proto/src/rpc";
import { combineClasses, format } from "@kikoff/utils/src/string";

import PageAlign from "@component/dashboard/offer/page_align";
import { PageRegistration } from "@component/flows/hooks/usePageFlow";
import StreamlinedHeader from "@component/onboarding/streamlined_header";
import { useOverlaysController } from "@src/overlay";
import { track } from "@util/analytics";

import styles from "./reconfirm_identity.module.scss";

interface ReconfirmIdentityPageProps extends PageRegistration {}

export default function ReconfirmIdentityPage({
  configFlow,
  Button,
  page,
}: ReconfirmIdentityPageProps) {
  const overlays = useOverlaysController();

  const [dob, setDob] = useState<string>();
  const [ssnLastFour, setSsnLastFour] = useState<string>();

  const user = useSelector((state) => state.user.proto);
  const { givenName, middleName, surname, phone } = user.info;

  useEffect(() => {
    webRPC.Reverification.getSensitivePii({}).then((response) => {
      setDob(response.iso8601Dob);
      setSsnLastFour(response.ssnLastFour);
    });
  }, []);

  configFlow.button(
    <Button
      onClick={() => {
        track("Identity Reverification - Start");
        webRPC.Reverification.startReverification({});
      }}
    >
      Verify
    </Button>
  );

  return (
    <PageAlign className={styles["reconfirm-identity"]} scroll>
      <StreamlinedHeader displayBackButton page={page} />
      <div className="mx-3">
        <header className="text:heading-3 mb-4" data-cy="reverify-title">
          Reconfirm or update your information.
        </header>
        <div className={styles.line} />
        <div className={styles["review-info-section"]}>
          {[
            {
              label: "Legal Name",
              value: `${givenName} ${middleName} ${surname}`,
            },
            {
              label: "Birthdate",
              value: format.date(parseISO(dob), "Mmmm ddd, yyyy"),
            },
            {
              label: "Home Address",
              value: format.address(user.info),
              onClick: () => {
                track.tap("Identity Reverification - Edit Address Button");
                overlays.push("user/change_address");
              },
            },
            {
              label: "Phone Number",
              value: format.phone(phone),
              onClick: () => {
                track.tap("Identity Reverification - Edit Phone Button");
                overlays.push("user/change_phone");
              },
            },
            {
              label: "Social Security Number",
              value: `***-**-${ssnLastFour}`,
            },
          ].map((step, i) => {
            return (
              <div className={styles.step} key={i}>
                <div className={styles.number}></div>
                <ContainerButton
                  onClick={step.onClick}
                  className={combineClasses(
                    styles.info,
                    step.onClick ? styles.cursor : ""
                  )}
                >
                  <div>
                    <div className={`text:regular+ ${styles.label}`}>
                      {step.label}
                    </div>
                    <div className={`text:regular ${styles.value}`}>
                      {step.value}
                    </div>
                  </div>
                  {step.onClick && (
                    <KButton
                      size="hug"
                      variant="text-underline"
                      className="text:regular+"
                    >
                      Edit
                    </KButton>
                  )}
                </ContainerButton>
              </div>
            );
          })}
        </div>
      </div>
    </PageAlign>
  );
}
